import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Copy, Trash2, Pencil, Eye, FileText, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Markdown, EmptyState, StatusBadge } from "@/components/common";
import { DatePicker } from "@/components/DatePicker";
import { api, errMsg, fmtDate, fmtDateTime } from "@/lib/api";

export const KIND_LABELS = {
  campaign: "Campaign plan", social_post: "Social posts", newsletter: "Newsletter", ad_copy: "Ad copy",
  seo_brief: "Content brief", keyword_list: "Keyword ideas", blog_post: "SEO blog post", page_meta: "Page titles & meta",
};
export const MARKETING_KINDS = ["campaign", "social_post", "newsletter", "ad_copy"];
export const SEO_KINDS = ["keyword_list", "seo_brief", "blog_post", "page_meta"];
const CHANNELS = ["LinkedIn", "Instagram", "Facebook", "X / Twitter", "TikTok", "Email", "Google Ads", "Meta Ads", "Blog", "Website"];

export function ContentDialog({ item, open, onOpenChange, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && item) { setF({ ...item, scheduled_date: item.scheduled_date || "" }); setEdit(false); } }, [open, item]);
  if (!f) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/content/${f.content_id}`, { title: f.title, body: f.body, channel: f.channel, scheduled_date: f.scheduled_date || "", status: f.status });
      toast.success("Saved");
      setEdit(false);
      onChanged?.(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this content?")) return;
    try { await api.delete(`/content/${f.content_id}`); toast.success("Deleted"); onOpenChange(false); onChanged?.(null); } catch (e) { toast.error(errMsg(e)); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(f.body); toast.success("Copied to clipboard"); } catch { toast.error("Copy failed"); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="content-dialog">
        <DialogHeader>
          <DialogTitle className="pr-6">{edit ? "Edit content" : f.title}</DialogTitle>
          <DialogDescription>{KIND_LABELS[f.kind]}{f.channel ? ` · ${f.channel}` : ""} · {f.created_by} · {fmtDateTime(f.created_at)}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="content-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["draft", "scheduled", "published"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Scheduled</Label><DatePicker value={f.scheduled_date} onChange={(v) => setF({ ...f, scheduled_date: v, status: v && f.status === "draft" ? "scheduled" : f.status })} allowClear testid="content-schedule-picker" /></div>
          <div className="space-y-1.5"><Label>Channel</Label><Input value={f.channel || ""} onChange={(e) => setF({ ...f, channel: e.target.value })} data-testid="content-channel-input" /></div>
        </div>
        {edit ? (
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="content-title-input" /></div>
            <div className="space-y-1.5"><Label>Content (markdown)</Label><Textarea rows={16} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="font-mono text-xs" data-testid="content-body-input" /></div>
          </div>
        ) : (
          <div className="bg-background border border-border rounded-xl p-4 sm:p-5" data-testid="content-body"><Markdown>{f.body}</Markdown></div>
        )}
        <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
          <Button variant="ghost" className="text-destructive sm:mr-auto" onClick={remove} data-testid="content-delete-button"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
          <Button variant="secondary" onClick={copy} data-testid="content-copy-button"><Copy className="h-4 w-4 mr-1" />Copy</Button>
          <Button variant="secondary" onClick={() => setEdit(!edit)} data-testid="content-edit-toggle">{edit ? <><Eye className="h-4 w-4 mr-1" />Preview</> : <><Pencil className="h-4 w-4 mr-1" />Edit</>}</Button>
          <Button onClick={save} disabled={busy} data-testid="content-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function GenerateDialog({ open, onOpenChange, kinds, onCreated, defaultKind }) {
  const [f, setF] = useState({ kind: defaultKind || kinds[0], channel: "", brief: "", scheduled_date: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF((x) => ({ ...x, kind: defaultKind || x.kind || kinds[0] })); }, [open, defaultKind, kinds]);
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/content/generate", { ...f, scheduled_date: f.scheduled_date || null });
      toast.success("Draft ready", { description: data.title });
      onOpenChange(false);
      setF({ ...f, brief: "" });
      onCreated?.(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="bg-card max-w-lg" data-testid="generate-dialog">
        <DialogHeader><DialogTitle>Create with AI</DialogTitle><DialogDescription>Saved as a draft you can edit. Nothing is posted or sent.</DialogDescription></DialogHeader>
        <form onSubmit={go} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                <SelectTrigger data-testid="generate-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>{kinds.map((k) => <SelectItem key={k} value={k}>{KIND_LABELS[k]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Channel</Label>
              <Select value={f.channel || "none"} onValueChange={(v) => setF({ ...f, channel: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="generate-channel-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Any / not set</SelectItem>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>What's it about?</Label>
            <Textarea required rows={4} value={f.brief} onChange={(e) => setF({ ...f, brief: e.target.value })}
              placeholder="e.g. Launch of our new website, aimed at marketing directors at mid-size brands" data-testid="generate-brief-input" />
          </div>
          <div className="space-y-1.5"><Label>Schedule on content calendar (optional)</Label><DatePicker value={f.scheduled_date} onChange={(v) => setF({ ...f, scheduled_date: v })} allowClear testid="generate-date-picker" /></div>
          {busy && <p className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Writing your draft. This usually takes 20–60 seconds.</p>}
          <DialogFooter><Button type="submit" disabled={busy} data-testid="generate-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}Generate</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ContentLibrary({ area, kinds }) {
  const [items, setItems] = useState(null);
  const [kind, setKind] = useState("all");
  const [open, setOpen] = useState(null);
  const [gen, setGen] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/content", { params: { area } }); setItems(data); } catch (e) { toast.error(errMsg(e)); setItems([]); }
  }, [area]);
  useEffect(() => { load(); }, [load]);
  const list = (items || []).filter((i) => kind === "all" || i.kind === kind);
  return (
    <div data-testid={`content-library-${area}`}>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["all", ...kinds].map((k) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`content-filter-${k}`}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${kind === k ? "bg-primary text-white border-primary" : "bg-card border-border hover:bg-muted"}`}>
            {k === "all" ? "All" : KIND_LABELS[k]}
          </button>
        ))}
        <Button size="sm" className="ml-auto" onClick={() => setGen(true)} data-testid="content-generate-button"><Sparkles className="h-4 w-4 mr-1.5" />Create with AI</Button>
      </div>
      {items === null ? <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> :
        list.length === 0 ? <div className="bg-card border border-border rounded-xl"><EmptyState icon={FileText} title="No content yet" text="Create something with AI, or ask your advisor in chat to draft and save it." /></div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {list.map((i) => (
              <button key={i.content_id} onClick={() => setOpen(i)} data-testid="content-card"
                className="text-left bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow flex flex-col">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary" className="font-normal">{KIND_LABELS[i.kind]}</Badge>
                  {i.channel && <span className="text-xs text-muted-foreground">{i.channel}</span>}
                  <span className="ml-auto"><StatusBadge status={i.status} testid="content-status" /></span>
                </div>
                <p className="font-medium mt-2 leading-snug line-clamp-2">{i.title}</p>
                <p className="text-xs text-muted-foreground mt-1.5 line-clamp-3">{i.body.replace(/[#*_>`|-]/g, "").slice(0, 220)}</p>
                <p className="text-[11px] text-muted-foreground mt-auto pt-3 flex items-center gap-1">
                  {i.scheduled_date ? <><CalendarClock className="h-3 w-3" />{fmtDate(i.scheduled_date, { weekday: "short", month: "short", day: "numeric" })}</> : `Draft · ${fmtDateTime(i.created_at)}`}
                </p>
              </button>
            ))}
          </div>
        )}
      <ContentDialog item={open} open={!!open} onOpenChange={(o) => !o && setOpen(null)} onChanged={(d) => { load(); if (d) setOpen(d); }} />
      <GenerateDialog open={gen} onOpenChange={setGen} kinds={kinds} onCreated={(d) => { load(); setOpen(d); }} />
    </div>
  );
}
